<script setup lang="ts">
const WrTag = defineAsyncComponent(
  () => import("~/components/molecules/WrTag.vue")
);

const route = useRoute();
const slug = route.params.slug;
const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/capabilities/${slug}-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}
useHead({
  title: data.value?.meta_tags?.title || "",
  meta: data.value?.meta_tags?.meta || [],
});
</script>

<template>
  <section
    v-if="data?.capability_content"
    class="max-w-[900px] md:mt-10 mx-auto px-4 py-10 max-sm:pt-0 relative z-10"
  >
    <NuxtImg
      :src="data?.capability_content?.image"
      height="285"
      width="856"
      loading="eager"
      class="mx-auto z-10 relative"
      :alt="(data?.capability_content?.heading, 'image')"
      preload
      format="webp"
    />

    <h1 class="mt-9 text-2xl lg:text-[36px] relative z-10 leading-[1.2]">
      <WrGradientText>
        {{ data?.capability_content?.heading }}
      </WrGradientText>
    </h1>

    <div class="flex flex-wrap gap-2 mt-5 relative z-10">
      <template v-for="tag in data.capability_content.tags" :key="tag">
        <div
          class="dark:block dark:bg-[linear-gradient(91.95deg,_#51277B_4.5%,_#8F5BC4_96.91%)] shadow-[1px_2px_5px_0px_#00000026] rounded-full"
        >
          <WrTag :tag="tag" />
        </div>
      </template>
    </div>

    <div
      v-if="
        data?.capability_content?.section &&
        data?.capability_content?.section?.length
      "
    >
      <div
        v-for="(section, index) in data?.capability_content?.section"
        :key="index"
        class="mt-5 text-lg max-md:text-sm"
      >
        <h2
          v-if="section.heading"
          class="lg:text-[27px] text-2xl font-bold mt-4"
        >
          <WrGradientText>
            {{ section?.heading }}
          </WrGradientText>
        </h2>
        <div
          class="[&_p]:leading-8 [&_p]:tracking-wider flex flex-col items-start gap-4"
          v-if="section?.text"
          v-html="section?.text"
        />
        <div
          v-if="index + 1 !== data?.capability_content.section?.length"
          class="flex justify-center mt-11 pb-2"
        >
          <span
            class="bg-[#0a0a0a] rounded-[50%] block size-[3px] m-[0px_9px]"
          />
          <span
            class="bg-[#0a0a0a] rounded-[50%] block size-[3px] m-[0px_9px]"
          />
          <span
            class="bg-[#0a0a0a] rounded-[50%] block size-[3px] m-[0px_9px]"
          />
        </div>
      </div>
    </div>

    <NuxtLink class="mt-5 block" to="/capabilities">
      <WrButton> Back to capabilities </WrButton>
    </NuxtLink>
  </section>

  <WrBannerCTA
    v-if="data?.capability_cta_content"
    :heading="data?.capability_cta_content?.title"
    :paragraph-text="data?.capability_cta_content?.subheading"
    :button-text="data?.capability_cta_content?.button_label"
    :changeContent="true"
  />

  <section
    class="relative pt-10 lg:pt-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20"
  >
    <h2 class="text-center mb-11">
      <WrGradientText text="Trusted for excellence and efficiency" />
      <p class="text-lg max-md:text-sm">
        WebReinvent is trusted by leading organisations to enhance their
        development and operational efficiency.
      </p>
    </h2>
    <WrTestimonialCardCarousel class="mb-12" />
    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7">
      View All
    </WrButton>
  </section>
</template>
